import type { ContentAccessMetadata } from '@cornerstone/shared';

// =============================================================================
// groupSections — 콘텐츠를 "어느 그룹의 것인가"로 묶는다 (2026-09-16, 대교 결정으로 단순화).
//
// 2026-09-16 대교: "공통이라는 분류는 없어지고 Owner가 올린 자료는 오너만 볼 수 있도록,
// 폴더 개념 없애자. 그냥 그룹별로 격리되는 것으로. 지정되지 않음도 없어지고, 폴더 이동도
// 없어지게. 그냥 단순하고 깔끔하게."
//
// 그래서 예전(2026-09-15 A안)에 있던 것들이 사라졌다. 왜 없앴는지 남겨 둔다 — 되살리지 말 것:
//   • '공통' 묶음 — 모두에게 보이는 중앙 자료라는 개념 자체를 화면에서 걷어냈다.
//     중앙(Owner) 소유 콘텐츠는 이제 중앙 권한이 있는 사람에게만, '내 자료'라는 이름으로 보인다.
//     (내부 플래그 sharedToAll은 그대로 살아 있다 — 대기 화면 게시·운영사 기본 템플릿이 그 위에서 돈다.)
//   • '미분류'·'다른 그룹'·'지정되지 않음' 머리 — 사용자가 고를 수도 없는 묶음이라 노이즈였다.
//     소유 그룹을 모르는 콘텐츠는 '기타' 하나로만 모으고, 그것도 내용이 있을 때만 그린다.
//   • folders 인자 — 폴더 개념이 화면에서 통째로 빠졌다(테이블·folderId 컬럼은 서버에 남아 있다).
//
// 규칙
//   • ownerGroupId === '<group>' → 그 그룹 묶음. 그룹 순서는 이름순.
//   • ownerGroupId === null / undefined(분류 전 옛 데이터) → 중앙 소유. 중앙 권한자에게만 '내 자료'로.
//   • 볼 수 있는 묶음이 하나뿐이면(예: A그룹 편집자) 머리를 두지 않는다 → null(평평한 목록).
//   • 묶음은 비어 있어도 자리를 지킨다 — 콘텐츠가 오갈 때마다 머리가 생겼다 사라지면
//     그 아래 버튼들이 움직인다("조작부는 움직이지 않는다").
// =============================================================================

export type ScopeGroup = { id: string; name: string };
export type GroupSection<I> = { key: string; label: string; central: boolean; items: I[] };

/** 중앙(Owner) 소유 콘텐츠 묶음의 이름. 2026-09-16 대교 결정으로 '공통' → '내 자료'. */
export const CENTRAL_SECTION_LABEL = '내 자료';
/** 이름을 모르는 그룹의 콘텐츠(보관된 그룹 등). 내용이 있을 때만 그린다. */
export const OTHER_SECTION_LABEL = '기타';

/** 중앙 소유인가 — 분류 전 옛 데이터(ownerGroupId undefined)도 그룹이 없으니 중앙으로 본다. */
function isCentralOwned(meta: ContentAccessMetadata): boolean {
  return typeof meta.ownerGroupId !== 'string';
}

/**
 * 이 사람 화면에 실제로 그려질 콘텐츠만 남긴다 (2026-09-16 대교 지적 3:
 * "라이브러리의 수는 2로 나오고, A그룹 입장에선 라이브러리에서 보이는건 1인 경우").
 *
 * 탭의 숫자도 **반드시 이 함수를 거친 배열의 길이**를 써야 한다. 목록과 숫자가 다른 배열을
 * 보면 또 어긋난다. 그룹별 권한을 '적용'하지 않은 조직에서는 서버가 조직 전체를 내려 주므로
 * (scopedContentRepos의 비적용 분기) 격리는 여기서 한 번 더 걸어야 한다.
 *
 * 중앙 권한자는 전부 본다. 그룹이 하나도 없는 조직은 격리할 대상 자체가 없으니 그대로 둔다.
 */
export function visibleContent<I extends ContentAccessMetadata>(
  items: I[],
  groups: ScopeGroup[],
  /** 중앙(Owner·조직 관리자) 권한이 있는가 — ScopeOptions.actor.central. */
  central: boolean,
): I[] {
  if (central || groups.length === 0) return items;
  const mine = new Set(groups.map((group) => group.id));
  // 중앙 소유(= 오너가 올린 자료)는 그룹 사용자에게 보이지 않는다 — 2026-09-16 대교 결정 3.
  return items.filter((item) => typeof item.ownerGroupId === 'string' && mine.has(item.ownerGroupId));
}

/**
 * 고르는 창(슬라이드 에디터의 미디어 선택 등)에 내놓을 미디어 (2026-09-29).
 *
 * 같은 파일을 여러 그룹이 각자 올리면(템플릿 그래픽 자동 업로드 등) 서버는 바이트를 하나로 묶지만
 * 목록에는 그룹마다 한 줄씩 남는다. 모든 그룹을 보는 중앙 관리자에게는 같은 그림이 그룹 수만큼 보였다.
 *
 * • 보이는 범위는 라이브러리와 똑같이 `visibleContent`로 먼저 거른다 — 하위 그룹 관리자에게
 *   상위(중앙) 자료나 맡지 않은 그룹의 자료가 선택지로 새어 나가지 않게.
 * • 내용(sha256)과 이름이 **둘 다** 같은 사본만 하나로 합친다. 이름이 다르면 편성 의도라 따로 둔다
 *   (서버의 중복 판정과 같은 기준). 해시가 아직 없는 옛 행은 합치지 않는다.
 * • 남길 사본은 `preferGroupId`(지금 만드는 콘텐츠의 소유 그룹) 것이 있으면 그것, 없으면 목록에서 먼저 나온 것.
 *   자리는 그 묶음이 처음 나온 위치를 지킨다.
 */
export function pickableMedia<I extends ContentAccessMetadata & { originalName: string; sha256?: string | null }>(
  items: I[],
  groups: ScopeGroup[],
  central: boolean,
  preferGroupId?: string | null,
): I[] {
  const result: I[] = [];
  const slot = new Map<string, number>();
  for (const item of visibleContent(items, groups, central)) {
    const key = item.sha256 ? `${item.sha256}\u0000${item.originalName}` : null;
    const index = key === null ? undefined : slot.get(key);
    if (index === undefined) {
      if (key !== null) slot.set(key, result.length);
      result.push(item);
    } else if (preferGroupId !== undefined && item.ownerGroupId === preferGroupId && result[index]!.ownerGroupId !== preferGroupId) {
      result[index] = item;
    }
  }
  return result;
}

/**
 * 소유 그룹별 묶음. 이미 `visibleContent`로 거른 배열을 넘긴다.
 * 묶음이 하나뿐이면 null — 호출자는 머리 없이 평평하게 그린다.
 */
export function groupSections<I extends ContentAccessMetadata>(
  items: I[],
  groups: ScopeGroup[],
  central: boolean,
): GroupSection<I>[] | null {
  const collator = new Intl.Collator('ko');
  const order: Array<{ key: string; label: string; central: boolean }> = [
    ...(central ? [{ key: 'central', label: CENTRAL_SECTION_LABEL, central: true }] : []),
    ...[...groups].sort((a, b) => collator.compare(a.name, b.name))
      .map((group) => ({ key: group.id, label: group.name, central: false })),
  ];
  // 머리가 하나(또는 없음)면 묶는 의미가 없다 — "그냥 단순하고 깔끔하게".
  if (order.length <= 1) return null;
  const bucket = new Map<string, GroupSection<I>>(order.map((entry) => [entry.key, { ...entry, items: [] }]));
  const other: GroupSection<I> = { key: 'other', label: OTHER_SECTION_LABEL, central: false, items: [] };
  for (const item of items) {
    const key = isCentralOwned(item) ? 'central' : item.ownerGroupId as string;
    (bucket.get(key) ?? other).items.push(item);
  }
  return [...order.map((entry) => bucket.get(entry.key)!), ...(other.items.length ? [other] : [])];
}

/**
 * 이 콘텐츠를 그 플레이리스트에 담을 수 있는가 — 담을 수 없으면 이유를 돌려준다(2026-09-18 대교).
 *
 * 서버 규칙(routes/playlists.ts assertItemsWithinGroup)과 같은 판정이다. 두 그룹을 맡은 사람은
 * 양쪽 자료를 다 볼 수 있어서, 막지 않으면 B그룹 플레이리스트에 A그룹 자료를 담게 된다. 중앙은
 * 넘나들되 담는 순간 그 그룹으로 복제되므로 막지 않는다. 분류 전 콘텐츠는 소유를 모르니 그대로 둔다.
 */
export function addToPlaylistBlockedReason(
  item: ContentAccessMetadata,
  playlistGroupId: string | null | undefined,
  central: boolean,
): string | null {
  if (central) return null;
  if (item.scopeClassified !== true) return null;
  if ((item.ownerGroupId ?? null) === (playlistGroupId ?? null)) return null;
  return '다른 그룹의 자료는 이 플레이리스트에 담을 수 없습니다.';
}
